import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  User, 
  LogOut, 
  Plus,
  ChevronRight,
  Menu,
  X,
  Calendar,
  ArrowLeftRight
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import NotificationBell from '../NotificationBell';

const LOGO_URL = "/logo.png";
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const sidebarLinks = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
  { path: '/dashboard/devis', icon: FileText, label: 'Mes devis' },
  { path: '/dashboard/planning', icon: Calendar, label: 'Planning' },
  { path: '/dashboard/messages', icon: MessageSquare, label: 'Messages' },
  { path: '/dashboard/profil', icon: User, label: 'Mon profil' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
  }, [location]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/unread/count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Tableau de bord';
    if (path.includes('/devis/')) return 'Détail du devis';
    if (path === '/dashboard/devis') return 'Mes devis';
    if (path === '/dashboard/messages') return 'Messages';
    if (path === '/dashboard/profil') return 'Mon profil';
    return '';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0A0A0A] border-r border-white/5 fixed h-full">
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <Link to="/dashboard">
            <img src={LOGO_URL} alt="Granipal" className="h-12 w-auto" />
          </Link>
        </div>

        {/* User Info */}
        <div className="p-6 border-b border-white/5">
          <p className="text-white font-semibold truncate">{user?.company_name}</p>
          <p className="text-white/40 text-sm truncate">{user?.contact_name}</p>
        </div>

        {/* New Quote Button */}
        <div className="p-4">
          <Link
            to="/configurateur"
            className="flex items-center justify-center gap-2 w-full btn-primary py-3"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4" />
            Nouveau devis
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const active = isActive(link.path, link.exact);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-4 py-3 transition-all duration-300 ${
                  active
                    ? 'bg-[#5D1F1F]/20 text-[#D4AF37] border-l-2 border-[#D4AF37]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`sidebar-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{link.label}</span>
                {link.path === '/dashboard/messages' && unreadCount > 0 && (
                  <span className="ml-auto bg-[#5D1F1F] text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/5 space-y-1">
          {user?.role === 'admin' && (
            <Link
              to="/admin"
              className="flex items-center gap-3 px-4 py-3 w-full text-white/60 hover:text-white hover:bg-[#D4AF37]/10 transition-colors rounded"
              data-testid="switch-to-admin"
            >
              <ArrowLeftRight className="w-5 h-5" />
              <span className="text-sm">Basculer vers Administration</span>
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-white/60 hover:text-red-400 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-white/5">
        <div className="flex items-center justify-between p-4">
          <Link to="/dashboard">
            <img src={LOGO_URL} alt="Granipal" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="bg-[#0A0A0A] border-b border-white/5 pb-4">
            <div className="px-4 py-3 border-b border-white/5 mb-2">
              <p className="text-white font-semibold">{user?.company_name}</p>
              <p className="text-white/40 text-sm">{user?.contact_name}</p>
            </div>
            <div className="px-4 mb-2">
              <Link
                to="/configurateur"
                className="flex items-center justify-center gap-2 w-full btn-primary py-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Plus className="w-4 h-4" />
                Nouveau devis
              </Link>
            </div>
            <nav className="space-y-1 px-2">
              {sidebarLinks.map((link) => {
                const Icon = link.icon;
                const active = isActive(link.path, link.exact);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 ${
                      active ? 'text-[#D4AF37]' : 'text-white/60'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{link.label}</span>
                    {link.path === '/dashboard/messages' && unreadCount > 0 && (
                      <span className="ml-auto bg-[#5D1F1F] text-white text-xs px-2 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
            <div className="px-2 mt-2 pt-2 border-t border-white/5">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 w-full text-white/60"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">Déconnexion</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        {/* Top Bar */}
        <div className="hidden lg:flex items-center justify-between bg-[#0A0A0A] border-b border-white/5 px-8 py-4 sticky top-0 z-40">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <Link to="/dashboard" className="text-white/40 hover:text-white transition-colors">
              Dashboard
            </Link>
            {getBreadcrumb() !== 'Tableau de bord' && (
              <>
                <ChevronRight className="w-4 h-4 text-white/20" />
                <span className="text-white">{getBreadcrumb()}</span>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <NotificationBell />
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-8 pt-20 lg:pt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
